/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import LazyLoad from 'react-lazy-load';
import utils from '../../../utils/utils';
import { AppContext } from '../../../context/appContext';

const Product = ({ data, onChange, value, showImage, hasImages }) => {
  const {
    id,
    name,
    description,
    unit,
    price,
    minAmount,
    maxAmount,
    imageUrl
  } = data;

  const [errors, setErrors] = useState({});
  const [settings] = useContext(AppContext);

  const generatePlaceholder = () => {
    return unit === 'kg' ? 'Hány kilót?' : 'Hány darabot?';
  };

  const removeFromErrors = (obj, entryName) => {
    const allEntries = Object.keys(obj);
    const response = {};

    allEntries.forEach(entry => {
      if (entry !== entryName) {
        response[entry] = obj[entry];
      }
    });

    return response;
  };

  const handleChange = (event, isDecimal) => {
    const itemName = event.target.name;
    let val = event.target.value;

    setErrors(removeFromErrors(errors, itemName));

    if (!isDecimal) {
      val = val.replace(/[^0-9]/g, '');
    } else {
      val = val.replace(/[,]/g, '.');
      val = val.replace(/[^0-9.]/g, '');
      val = val.replace(/\.(?=.*\.)/g, '');

      const decimalIndex = val.indexOf('.');
      if (decimalIndex !== -1) {
        val = val.substr(0, decimalIndex + 2);
      }
    }

    onChange(itemName, val);
  };

  const checkAmount = event => {
    if (!event.target.value) return;

    let max = maxAmount;
    if (!maxAmount) max = 50;

    if (event.target.value > max) {
      setErrors({
        ...errors,
        [event.target.name]: `Maximálisan vásárolható mennyiség ${max}!`
      });
      onChange(event.target.name, '');
    }

    if (minAmount && event.target.value < minAmount) {
      setErrors({
        ...errors,
        [event.target.name]: `Minimálisan vásárolható mennyiség ${minAmount}!`
      });
      onChange(event.target.name, '');
    }

    if (event.target.value < 0.1) {
      onChange(event.target.name, '');
    }
  };

  return (
    <LazyLoad debounce={false} offsetVertical={600}>
      <Root>
        {hasImages && (
          <ImageWrapper>
            {imageUrl && (
              <img
                src={imageUrl}
                alt={`${name}_image`}
                onClick={() => showImage(imageUrl)}
              />
            )}
          </ImageWrapper>
        )}
        <Label htmlFor={id}>
          <span>{name}</span>
          <span className="separator" />
          <span>
            {utils.priceMask(price)} Ft / {unit}
          </span>
          <Subtitle>{description}</Subtitle>
        </Label>
        <InputWrapper bgcolor={settings?.productListHeader || `#e7c37a`}>
          <input
            id={id}
            name={id}
            max={maxAmount}
            value={value}
            onChange={event => handleChange(event, unit === 'kg')}
            onBlur={checkAmount}
            placeholder={generatePlaceholder()}
          />
          {errors[id] && <ErrorText>{errors[id]}</ErrorText>}
        </InputWrapper>
      </Root>
    </LazyLoad>
  );
};

export default Product;

Product.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    description: PropTypes.string,
    unit: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    minAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    maxAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    imageUrl: PropTypes.string
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showImage: PropTypes.func.isRequired,
  hasImages: PropTypes.bool.isRequired
};

Product.defaultProps = {
  value: ''
};

const Root = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;

  & span {
    color: #1c2f3c;
  }

  @media (max-width: 675px) {
    flex-direction: column;
    font-size: 0.8em;
  }
`;

const ImageWrapper = styled.div`
  width: 140px;
  margin-right: 24px;

  & img {
    width: 100%;
    height: auto;
  }

  @media (max-width: 675px) {
    height: auto;
    width: 70%;
    margin: 0 auto;
  }
`;

const Label = styled.label`
  display: flex;
  flex: 2;
  flex-wrap: wrap;
  width: 100%;

  align-items: center;
  font-weight: 600;
  font-size: 1.15em;
  color: #1c2f3c;

  & span {
    margin-right: 8px;
  }

  & .separator {
    flex: 1;
    border-bottom: dotted 2px #333;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-start;
  flex-direction: column;
  align-items: flex-end;
  height: 60px;
  width: 100%;

  @media (max-width: 675px) {
    justify-content: flex-end;
  }

  & input {
    background-color: ${props => props.bgcolor};
    border-width: 0px 0px 0px 0px;
    border-radius: 25px 25px 25px 25px;
    padding: 0.75em;
    font-size: 1em;
    box-sizing: border-box;
    transition: all 0.1s linear;
    width: 60%;

    &:focus {
      outline: none;
      box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1);
    }
  }
`;

const Subtitle = styled.span`
  width: 100%;
  font-size: 0.8em;
  font-weight: 400;
  margin: 6px 0;
`;

const ErrorText = styled.span`
  && {
    color: red;
    font-size: 14px;
  }
`;
